// Web FCM client. Inert when VITE_FIREBASE_* env vars are not set —
// `isPushAvailable()` returns false and the rest of the app keeps working.
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, deleteToken, onMessage } from 'firebase/messaging';

const SW_PATH = '/firebase-messaging-sw.js';

function readConfig() {
  const env = import.meta.env || {};
  const config = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  };
  const vapidKey = env.VITE_FIREBASE_VAPID_KEY;
  // Every value must be non-empty to be considered configured.
  const hasAll = Object.values(config).every((v) => typeof v === 'string' && v.length > 0)
    && typeof vapidKey === 'string' && vapidKey.length > 0;
  return hasAll ? { config, vapidKey } : null;
}

export function isPushAvailable() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('Notification' in window)) return false;
  return readConfig() !== null;
}

async function registerServiceWorker(config) {
  // Pass config via query string — the SW reads it from its own URL
  // (it has no access to import.meta.env).
  const params = new URLSearchParams({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
  });
  return navigator.serviceWorker.register(`${SW_PATH}?${params.toString()}`);
}

function getFirebaseApp(config) {
  return getApps().length > 0 ? getApps()[0] : initializeApp(config);
}

/**
 * Asks the browser for notification permission, registers the FCM service
 * worker, and returns the device FCM token (or null if permission denied / not
 * supported).
 */
export async function requestPushPermission() {
  if (!isPushAvailable()) return null;
  const { config, vapidKey } = readConfig();

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const swRegistration = await registerServiceWorker(config);
  const messaging = getMessaging(getFirebaseApp(config));
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: swRegistration,
  });
  return token || null;
}

/**
 * Returns the current FCM token only when the browser has already granted
 * notification permission. This is safe to call on app startup because it never
 * opens the browser permission prompt.
 */
export async function getPushTokenIfPermissionGranted() {
  if (!isPushAvailable()) return null;
  if (Notification.permission !== 'granted') return null;

  const { config, vapidKey } = readConfig();
  const swRegistration = await registerServiceWorker(config);
  const messaging = getMessaging(getFirebaseApp(config));
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: swRegistration,
  });
  return token || null;
}

/**
 * Deletes the FCM token from the device. The backend record is removed
 * separately via DELETE /api/push/token.
 */
export async function revokePushToken() {
  if (!isPushAvailable()) return;
  const { config } = readConfig();
  try {
    const messaging = getMessaging(getFirebaseApp(config));
    await deleteToken(messaging);
  } catch (err) {
    // Non-fatal — we still remove from the server.
    console.warn('FCM deleteToken failed:', err);
  }
}

/**
 * Listens for FCM messages while the app tab is open and focused. Background
 * delivery is handled separately by public/firebase-messaging-sw.js.
 */
export function subscribeToForegroundPushMessages(handler) {
  if (!isPushAvailable() || typeof handler !== 'function') {
    return () => {};
  }

  try {
    const { config } = readConfig();
    const messaging = getMessaging(getFirebaseApp(config));
    return onMessage(messaging, handler);
  } catch (err) {
    console.warn('FCM foreground listener failed:', err);
    return () => {};
  }
}
